export const COMPUTE_CRM_API = "/api/wallet/accounts?action=compute_crm";

export type CRMConnectionStatus = "healthy" | "stale" | "needs_attention";
export type CRMResourceStatus = "running" | "stopped" | "launching" | "failed" | "unknown";
export type CRMRecommendationPriority = "low" | "medium" | "high";
export type CRMActionStatus = "requested" | "approved" | "running" | "complete" | "failed";
export type CRMAlertStatus = "open" | "acknowledged" | "dismissed" | "resolved";

export type CRMProviderConnection = {
  id: string;
  providerSlug: string;
  providerName: string;
  displayName: string;
  authType: string;
  permissionMode: string;
  status: CRMConnectionStatus;
  lastSuccessAt?: string;
  lastError?: string;
};

export type CRMSyncDiagnostic = {
  id: string;
  connectionId: string;
  providerSlug: string;
  providerName: string;
  status: "success" | "failed";
  resourceCount: number;
  error?: string;
  checkedAt: string;
};

export type CRMWalletSnapshot = {
  id: string;
  connectionId: string;
  providerSlug: string;
  balanceUsd: number | null;
  spend7dUsd: number | null;
  burnRateUsdDay: number | null;
  runwayDays: number | null;
  buyingPowerGpuHours: number | null;
  source: string;
  takenAt: string;
};

export type CRMResource = {
  id: string;
  connectionId: string;
  providerSlug: string;
  providerName: string;
  providerResourceId: string;
  resourceType: string;
  name: string;
  region?: string;
  status: CRMResourceStatus;
  providerStatus?: string;
  gpuType?: string;
  gpuCount: number;
  image?: string;
  purpose: string;
  containerDiskGb: number | null;
  volumeGb: number | null;
  storageId?: string;
  storageMountPath?: string;
  cpuUtilizationPercent: number | null;
  gpuUtilizationPercent: number | null;
  memoryUtilizationPercent: number | null;
  diskUtilizationPercent: number | null;
  metricSource?: string;
  metricsSampledAt?: string;
  runtimeUrl?: string;
  hourlyPriceUsd: number | null;
  lastSeenAt: string;
  tags: Record<string, string>;
};

export type CRMWorkload = {
  id: string;
  connectionId: string;
  resourceId: string;
  providerSlug: string;
  name: string;
  workloadType: string;
  status: CRMResourceStatus;
  runtimeSeconds: number;
  costSoFarUsd: number | null;
  purpose: string;
  cpuUtilizationPercent: number | null;
  gpuUtilizationPercent: number | null;
  metricSource?: string;
  owner: string;
  startedAt: string;
  updatedAt: string;
};

export type CRMRecommendation = {
  id: string;
  type: string;
  priority: CRMRecommendationPriority;
  title: string;
  description: string;
  estimatedSavingUsdMonth: number | null;
  connectionId?: string;
  resourceId?: string;
  status: "open" | "dismissed" | "actioned";
  actionPayload?: Record<string, unknown>;
  createdAt: string;
};

export type CRMAlert = {
  id: string;
  type: string;
  severity: CRMRecommendationPriority;
  status: CRMAlertStatus;
  title: string;
  message: string;
  sourceType?: "connection" | "resource" | "recommendation" | "wallet" | "pooled_demand" | "catalog";
  sourceId?: string;
  ruleId?: string;
  createdAt: string;
  updatedAt: string;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  dismissedAt?: string;
  dismissedBy?: string;
  resolvedAt?: string;
};

export type CRMAction = {
  id: string;
  workspaceId: string;
  requestedByUserId: string;
  requestedByEmail: string;
  actionType: string;
  status: CRMActionStatus;
  connectionId?: string;
  resourceId?: string;
  payload: Record<string, unknown>;
  result?: Record<string, unknown>;
  createdAt: string;
  completedAt?: string;
};

export type CRMPortfolio = {
  providerConnections: number;
  healthyConnections: number;
  resources: number;
  runningResources: number;
  workloads: number;
  activeWorkloads: number;
  recommendations: number;
  alerts: number;
  committedGpuCount: number;
  committedGpuHours: number;
  metricEnabledResources: number;
  unmeteredRunningResources: number;
};

export type ComputeCRMResponse = {
  portfolio: CRMPortfolio;
  connections: CRMProviderConnection[];
  walletSnapshots: CRMWalletSnapshot[];
  syncDiagnostics: CRMSyncDiagnostic[];
  resources: CRMResource[];
  workloads: CRMWorkload[];
  recommendations: CRMRecommendation[];
  alerts: CRMAlert[];
  actions: CRMAction[];
  generatedAt: string;
};

export function normalizeCRMResponse(raw: Partial<ComputeCRMResponse> | null | undefined): ComputeCRMResponse {
  return {
    portfolio: {
      providerConnections: Number(raw?.portfolio?.providerConnections || 0),
      healthyConnections: Number(raw?.portfolio?.healthyConnections || 0),
      resources: Number(raw?.portfolio?.resources || 0),
      runningResources: Number(raw?.portfolio?.runningResources || 0),
      workloads: Number(raw?.portfolio?.workloads || 0),
      activeWorkloads: Number(raw?.portfolio?.activeWorkloads || 0),
      recommendations: Number(raw?.portfolio?.recommendations || 0),
      alerts: Number(raw?.portfolio?.alerts || 0),
      committedGpuCount: Number(raw?.portfolio?.committedGpuCount || 0),
      committedGpuHours: Number(raw?.portfolio?.committedGpuHours || 0),
      metricEnabledResources: Number(raw?.portfolio?.metricEnabledResources || 0),
      unmeteredRunningResources: Number(raw?.portfolio?.unmeteredRunningResources || 0),
    },
    connections: Array.isArray(raw?.connections) ? raw.connections : [],
    walletSnapshots: Array.isArray(raw?.walletSnapshots) ? raw.walletSnapshots : [],
    syncDiagnostics: Array.isArray((raw as any)?.syncDiagnostics) ? (raw as any).syncDiagnostics : [],
    resources: Array.isArray(raw?.resources) ? raw.resources : [],
    workloads: Array.isArray(raw?.workloads) ? raw.workloads : [],
    recommendations: Array.isArray(raw?.recommendations) ? raw.recommendations : [],
    alerts: Array.isArray(raw?.alerts) ? raw.alerts : [],
    actions: Array.isArray(raw?.actions) ? raw.actions : [],
    generatedAt: String(raw?.generatedAt || ""),
  };
}
